import {Component, ViewChild} from '@angular/core';
import {Barchart} from "../../shared/models/chart.model";
import moment from "moment/moment";
import {AnalyticsService} from '../../shared/services/analytics.service';
import {ExportService, ExportConfig} from '../../shared/services/export.service';
import appConfig from "../../shared/config/app-config";
import { DatatableComponent } from '../../shared/components/datatable/datatable.component';
import { ColDef } from 'ag-grid-community';
import { createColumn, createDateColumn } from '../../utils/helper';

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrl: './analytics-dashboard.component.scss'
})

export class AnalyticsDashboardComponent {

  @ViewChild(DatatableComponent) datatable!: DatatableComponent;

  dateRange = {
    start: moment().subtract(29, 'days'),
    end: moment()
  };

  totalViewsChart: Barchart = { element: "totalViewsChart", data: [] };
  tilesData: any[] = [];
  advertisingCategories: any[] = [];
  deviceAccess: any[] = [];
  geoLocation: any[] = [];
  overviewData: any = {};
  trafficSource: any[] = [];
  trafficSourceTabularData: any[] = [];

  readonly advertisingCategoriesColorCode = ["#C6E2DD", "#B0D4CD", "#DCE9A9", "#E5EFBD"];
  readonly deviceAccessColorCode = ["#AFB9F1", "#0A3D49", "#AA1B5C", "#9DA6A8"];
  readonly url = `${appConfig.analyticsBaseUrl}analytics/articles/top-count`;
  readonly colDefs: ColDef[] = [
    createColumn('Article Title', 'articleData.title', {flex: 1}),
    createColumn('Views', 'count'),
    createColumn('Page Type', 'articleData.post_type'),
    createDateColumn('Publish Date', 'articleData.date', true),
    createColumn('Type', 'articleData.page_template'),
    createColumn('Author', 'articleData.authors.0.name'),
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private exportService: ExportService
  ) {}

  onDateRangeChanged({ start, end }: { start: string, end: string }) {
    this.dateRange = { start: moment(start), end: moment(end) };
    this.tracking();
  }

  tracking() {
    const { start, end } = this.dateRange;
    this.analyticsService.httpGet(`analytics/overview?start_date=${start.format('YYYY-MM-DD')}&end_date=${end.format('YYYY-MM-DD')}`).subscribe((res: any) => {
      this.overviewData = res.data

      this.tilesData = Object.values(this.overviewData.tilesData).map((item: any) => {
        Math.abs(item.percentageChange);
        return item;
      });

      this.advertisingCategories = this.transformChartData(this.overviewData.advertisingCategories, 'category');
      this.totalViewsChart = this.setBarChartData('totalViewsChart', this.overviewData.pageViews);
      this.deviceAccess = this.transformChartData(this.overviewData.deviceAccess, 'category');
      this.geoLocation = this.transformGeolocationData();
      this.trafficSource = this.overviewData.trafficSource
      this.trafficSourceTabularData = this.getTrafficSourceTabularData()
    })
  }

  getTrafficSourceTabularData() {
    return [
      {
        "source": "Total",
        "sessions": 140346,
        "engagedSessions": 64698,
        "engagementRate": 46.1,
        "avgEngagement": 59,
        "eventsPerSessions": 9.23
      },
      {
        "source": "Email",
        "sessions": 70583,
        "engagedSessions": 36852,
        "engagementRate": 52.21,
        "avgEngagement": 52,
        "eventsPerSessions": 7.53
      },
      {
        "source": "Direct Search",
        "sessions": 28200,
        "engagedSessions": 12741,
        "engagementRate": 52.21,
        "avgEngagement": 52,
        "eventsPerSessions": 7.53
      },
      {
        "source": "Referals",
        "sessions": 14801,
        "engagedSessions": 6584,
        "engagementRate": 52.21,
        "avgEngagement": 52,
        "eventsPerSessions": 7.53
      },
      {
        "source": "Social",
        "sessions": 13398,
        "engagedSessions": 3881,
        "engagementRate": 52.21,
        "avgEngagement": 52,
        "eventsPerSessions": 7.53
      },
      {
        "source": "Other",
        "sessions": 3939,
        "engagedSessions": 2091,
        "engagementRate": 52.21,
        "avgEngagement": 52,
        "eventsPerSessions": 7.53
      }

    ]
  }

  transformGeolocationData(): { country: string; count: number; percentage: number }[] {

    const sortedEntries: [string, number][] = Object.entries(this.overviewData.geolocation || {}) as [string, number][];

    sortedEntries.sort(([, a], [, b]) => b - a);

    const maxValue: number = sortedEntries.length > 0 ? sortedEntries[0][1] : 0;

    return sortedEntries.map(([country, count]) => {
      const normalized = (Math.log(count + 1) / Math.log(maxValue + 1)) * 85;
      const percentage = 15 + normalized;

      return {
        country,
        count,
        percentage: Math.min(100, Math.max(15, percentage))
      };
    });
  }

  private transformChartData(data: any, keyName: string) {
    return Object.entries(data).map(([key, value]) => ({ [keyName]: key, value }));
  }

  private setBarChartData(element: string, data: any) {
    return { element, data: [{ category: '', ...data }] };
  }

  /**
   * Export traffic source data to Excel
   */
  async exportToExcel() {
    if (!this.overviewData.trafficSourceDetails || this.overviewData.trafficSourceDetails.length === 0) {
      console.warn('No data available to export');
      return;
    }

    const exportData = this.prepareExportData();
    const config = this.getExportConfig(exportData);

    try {
      const result = await this.exportService.exportToExcel(config);
      if (result.success) {
        console.log(`Excel file "${result.filename}" exported successfully`);
      } else {
        console.error('Excel export failed:', result.error);
      }
    } catch (error) {
      console.error('Excel export error:', error);
    }
  }

  private prepareExportData(): any[] {
    return this.overviewData.trafficSourceDetails.map((item: any) => ({
      'Source': item.trafficSourceType || 'N/A',
      'Sessions': this.exportService.formatNumber(item.totalSessions),
      'Total Events': this.exportService.formatNumber(item.totalEvents),
      'Events Per Sessions (Avg)': Number(item.averageEventsPerSession).toFixed(2),
    }));
  }

  private getExportConfig(exportData: any[]): ExportConfig {
    return {
      data: exportData,
      filename: `Traffic_Source_Analytics_${this.dateRange.start.format('MMM-DD')}_to_${this.dateRange.end.format('MMM-DD-YYYY')}`,
      sheetName: 'Traffic Source Data',
      columnWidths: [20, 15, 15, 25, 25]
    };
  }
}
